import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FilterSidebar } from '../components/FilterSidebar';
import { ProductCard } from '../components/ProductCard';
import { products } from '../data/products';
import { FilterState } from '../types/product';
import { Filter, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Catalog() {
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    brands: [],
    priceRange: [0, 5000],
    search: ''
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  // Filter Logic
  const filteredProducts = products.filter(product => {
    const matchCategory = filters.categories.length === 0 || filters.categories.includes(product.category);
    const matchBrand = filters.brands.length === 0 || filters.brands.includes(product.brand);
    const matchPrice = product.price >= filters.priceRange[0] && product.price <= filters.priceRange[1];
    return matchCategory && matchBrand && matchPrice;
  });
  // Sort Logic
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'name':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });
  return <div className="bg-[#f8f9fa] min-h-screen" data-id="element-297">
      {/* Breadcrumbs */}
      <div className="bg-white border-b" data-id="element-298">
        <div className="max-w-7xl mx-auto px-4 py-3" data-id="element-299">
          <div className="flex items-center gap-2 text-sm" data-id="element-300">
            <Link to="/" className="text-gray-500 hover:text-[#0056b3]" data-id="element-301">
              Inicio
            </Link>
            <span className="text-gray-300" data-id="element-302">/</span>
            <span className="text-gray-900 font-medium" data-id="element-303">Productos</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b" data-id="element-304">
        <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-305">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-306">
            Catálogo de Productos
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-307">
            Todo lo que necesitas para tu hogar y proyectos
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-308">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-white p-4 rounded-lg border border-gray-100" data-id="element-309">
          <div className="flex items-center gap-4" data-id="element-310">
            <button className="md:hidden flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50" onClick={() => setIsMobileFilterOpen(true)} data-id="element-311">
              <Filter className="w-4 h-4" data-id="element-312" />
              Filtros
            </button>
            <span className="text-sm text-gray-500" data-id="element-313">
              {sortedProducts.length} productos encontrados
            </span>
          </div>

          <div className="flex items-center gap-2" data-id="element-314">
            <span className="text-sm text-gray-500" data-id="element-315">Ordenar por:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0056b3] focus:border-transparent" data-id="element-316">
              <option value="recent" data-id="element-317">Más recientes</option>
              <option value="price-asc" data-id="element-318">Precio: Menor a Mayor</option>
              <option value="price-desc" data-id="element-319">Precio: Mayor a Menor</option>
              <option value="name" data-id="element-320">Nombre A-Z</option>
            </select>
          </div>
        </div>

        <div className="flex gap-6" data-id="element-321">
          {/* Sidebar - Desktop */}
          <aside className="w-64 flex-shrink-0 hidden md:block" data-id="element-322">
            <div className="sticky top-28" data-id="element-323">
              <FilterSidebar filters={filters} setFilters={setFilters} data-id="element-324" />
            </div>
          </aside>

          {/* Mobile Filter Drawer */}
          <AnimatePresence data-id="element-325">
            {isMobileFilterOpen && <>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 0.5
            }} exit={{
              opacity: 0
            }} onClick={() => setIsMobileFilterOpen(false)} className="fixed inset-0 bg-black z-40 md:hidden" data-id="element-326" />
                <motion.div initial={{
              x: '-100%'
            }} animate={{
              x: 0
            }} exit={{
              x: '-100%'
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="fixed left-0 top-0 h-full w-[85%] max-w-sm bg-white z-50 md:hidden flex flex-col" data-id="element-327">
                  <div className="p-4 border-b flex justify-between items-center" data-id="element-328">
                    <h2 className="font-bold text-lg" data-id="element-329">Filtros</h2>
                    <button onClick={() => setIsMobileFilterOpen(false)} className="p-2" data-id="element-330">
                      <X className="w-5 h-5 text-gray-500" data-id="element-331" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4" data-id="element-332">
                    <FilterSidebar filters={filters} setFilters={setFilters} data-id="element-333" />
                  </div>
                  <div className="p-4 border-t" data-id="element-334">
                    <button onClick={() => setIsMobileFilterOpen(false)} className="w-full py-3 bg-[#0056b3] text-white rounded-md font-medium" data-id="element-335">
                      Ver {sortedProducts.length} productos
                    </button>
                  </div>
                </motion.div>
              </>}
          </AnimatePresence>

          {/* Product Grid */}
          <div className="flex-1" data-id="element-336">
            {sortedProducts.length > 0 ? <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-337">
                {sortedProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-338" />)}
              </div> : <div className="text-center py-16 bg-white rounded-lg border border-gray-100" data-id="element-339">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-340">
                  <Filter className="w-8 h-8 text-gray-400" data-id="element-341" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-342">
                  No se encontraron productos
                </h3>
                <p className="text-gray-500 mb-4" data-id="element-343">
                  Intenta ajustar los filtros de búsqueda
                </p>
                <button onClick={() => setFilters({
              categories: [],
              brands: [],
              priceRange: [0, 5000],
              search: ''
            })} className="text-[#0056b3] font-medium hover:underline" data-id="element-344">
                  Limpiar filtros
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}